<div class="panel-header">
  <span class="panel-title">CORRELATION & REGRESSION</span>
</div>

@if (!hasData()) {
  <div class="panel-empty">Add at least 2 data pairs</div>
} @else {
  <!-- ── Correlation (r) ── -->
  <div class="measure-section">
    <div class="section-header">
      <span class="section-label">Correlation (r)</span>
      <dojo-icon
        class="section-info"
        icon="circle-info"
        [size]="12"
        [matTooltip]="rTooltip"
        matTooltipPosition="right"
        matTooltipClass="edu-tooltip"
      />
    </div>

    <div class="stat-row">
      <dojo-icon [icon]="rArrowIcon()" [size]="16" [style.color]="rColor()" />
      <span class="stat-value" [style.color]="rColor()">{{ rFormatted() }}</span>
      <span class="stat-badge" [style.color]="rColor()">{{ rStrengthLabel() }}</span>
    </div>

    <mat-expansion-panel
      class="calc-panel"
      hideToggle
      [expanded]="rExpanded()"
      (opened)="onSectionOpened('r')"
      (closed)="onSectionClosed('r')"
    >
      <mat-expansion-panel-header [collapsedHeight]="'28px'" [expandedHeight]="'28px'">
        <mat-panel-title class="calc-panel-title">
          <span>{{ rExpanded() ? 'Hide calculation' : 'Show calculation' }}</span>
          <dojo-icon [icon]="rExpanded() ? 'chevron-up' : 'chevron-down'" [size]="10" />
        </mat-panel-title>
      </mat-expansion-panel-header>

      <ng-template matExpansionPanelContent>
        <div class="card-detail">
          <div class="card-formula">
            <dojo-markdown-viewer [content]="rCombinedFormula()" />
          </div>

          <!-- Correlation Strength Scale -->
          <div class="correlation-scale">
            <div class="scale-zone-labels">
              <span class="zone-label">Negative</span>
              <span class="zone-label zone-label--center">None</span>
              <span class="zone-label">Positive</span>
            </div>
            <div class="scale-bar">
              <div class="seg seg--neg-strong" [class.seg--active]="correlationScale().zone === 'strong-neg'"></div>
              <div class="seg seg--neg-weak" [class.seg--active]="correlationScale().zone === 'weak-neg'"></div>
              <div class="seg seg--none" [class.seg--active]="correlationScale().zone === 'none'"></div>
              <div class="seg seg--pos-weak" [class.seg--active]="correlationScale().zone === 'weak-pos'"></div>
              <div class="seg seg--pos-strong" [class.seg--active]="correlationScale().zone === 'strong-pos'"></div>
              <div class="scale-needle" [style.left.%]="correlationScale().position"></div>
            </div>
            <div class="scale-ticks">
              <span class="tick" style="left: 0%">−1</span>
              <span class="tick" style="left: 12.5%">−0.75</span>
              <span class="tick" style="left: 37.5%">−0.25</span>
              <span class="tick tick--zero" style="left: 50%">0</span>
              <span class="tick" style="left: 62.5%">0.25</span>
              <span class="tick" style="left: 87.5%">0.75</span>
              <span class="tick" style="left: 100%">1</span>
            </div>
          </div>
        </div>
      </ng-template>
    </mat-expansion-panel>
  </div>

  <!-- ── R-squared ── -->
  <div class="measure-section">
    <div class="section-header">
      <span class="section-label">R-squared (r²)</span>
      <dojo-icon
        class="section-info"
        icon="circle-info"
        [size]="12"
        [matTooltip]="rSquaredTooltip"
        matTooltipPosition="right"
        matTooltipClass="edu-tooltip"
      />
    </div>

    <div class="stat-row">
      <span class="stat-value" [style.color]="rColor()">{{ rSquaredFormatted() }}</span>
      <span class="stat-badge" [style.color]="rColor()">{{ varianceExplained() }}</span>
    </div>

    <mat-expansion-panel
      class="calc-panel"
      hideToggle
      [expanded]="rSquaredExpanded()"
      (opened)="onSectionOpened('rSquared')"
      (closed)="onSectionClosed('rSquared')"
    >
      <mat-expansion-panel-header [collapsedHeight]="'28px'" [expandedHeight]="'28px'">
        <mat-panel-title class="calc-panel-title">
          <span>{{ rSquaredExpanded() ? 'Hide calculation' : 'Show calculation' }}</span>
          <dojo-icon [icon]="rSquaredExpanded() ? 'chevron-up' : 'chevron-down'" [size]="10" />
        </mat-panel-title>
      </mat-expansion-panel-header>

      <ng-template matExpansionPanelContent>
        <div class="card-detail">
          <div class="card-formula">
            <dojo-markdown-viewer [content]="rSquaredCombinedFormula()" />
          </div>
        </div>
      </ng-template>
    </mat-expansion-panel>
  </div>

  <!-- ── Regression Equation ── -->
  <div class="measure-section">
    <div class="section-header" (mouseenter)="onSectionMouseEnter('equation')" (mouseleave)="onSectionMouseLeave()">
      <span class="section-label">Regression Equation</span>
      <dojo-icon
        class="section-info"
        icon="circle-info"
        [size]="12"
        [matTooltip]="equationTooltip"
        matTooltipPosition="right"
        matTooltipClass="edu-tooltip"
      />
    </div>

    <div class="equation-display" (mouseenter)="onSectionMouseEnter('equation')" (mouseleave)="onSectionMouseLeave()">
      <dojo-markdown-viewer [content]="equationLatex()" />
    </div>
    <div class="equation-details">
      <div
        class="equation-detail hoverable-detail"
        (mouseenter)="onSectionMouseEnter('slope')"
        (mouseleave)="onSectionMouseLeave()"
      >
        <dojo-markdown-viewer
          class="equation-detail-item"
          [content]="'$a \\text{ (slope)} = ' + slopeFormatted() + '$'"
        />
      </div>
      <div
        class="equation-detail hoverable-detail"
        (mouseenter)="onSectionMouseEnter('intercept')"
        (mouseleave)="onSectionMouseLeave()"
      >
        <dojo-markdown-viewer
          class="equation-detail-item"
          [content]="'$b \\text{ (intercept)} = ' + interceptFormatted() + '$'"
        />
      </div>
    </div>

    <mat-expansion-panel
      class="calc-panel"
      hideToggle
      [expanded]="equationExpanded()"
      (opened)="onSectionOpened('equation')"
      (closed)="onSectionClosed('equation')"
    >
      <mat-expansion-panel-header [collapsedHeight]="'28px'" [expandedHeight]="'28px'">
        <mat-panel-title class="calc-panel-title">
          <span>{{ equationExpanded() ? 'Hide calculation' : 'Show calculation' }}</span>
          <dojo-icon [icon]="equationExpanded() ? 'chevron-up' : 'chevron-down'" [size]="10" />
        </mat-panel-title>
      </mat-expansion-panel-header>

      <ng-template matExpansionPanelContent>
        <div class="card-detail">
          <div class="card-formula">
            <dojo-markdown-viewer [content]="equationDerivationFormula()" />
          </div>
        </div>
      </ng-template>
    </mat-expansion-panel>
  </div>

  <!-- ── Interpretation ── -->
  @if (interpretationLines().length > 0) {
    <div class="measure-section measure-section--no-border">
      <div class="section-header">
        <span class="section-label">Interpretation</span>
      </div>
      @for (line of interpretationLines(); track $index) {
        <div class="interpretation-line">{{ line }}</div>
      }
    </div>
  }

  <!-- ── Prediction ── -->
  <div class="prediction-section">
    <div class="section-header">
      <span class="section-label">Predict</span>
    </div>
    <form class="prediction-input-row" (submit)="$event.preventDefault(); onPredict()">
      <div class="prediction-field">
        <dojo-number-input [formCtrl]="predictionXCtrl" [placeholder]="'Enter ' + xLabel() + ' value'" [size]="'s'" />
      </div>
      <dojo-button
        [size]="'s'"
        [fill]="'solid'"
        customColor="sky"
        [type]="'submit'"
        [disabled]="predictionXCtrl.value === null"
      >
        <dojo-icon icon="wand-magic-sparkles" [size]="11" faPrefix="fas" class="mr-1" />
        Predict
      </dojo-button>
    </form>

    @if (predictionResult(); as pred) {
      <div class="prediction-result">
        <div class="prediction-result-header">
          <div class="prediction-type" [class.prediction-type--warn]="pred.isExtrapolation">
            @if (pred.isExtrapolation) {
              Extrapolation — outside observed range
            } @else {
              Interpolation — within observed range
            }
          </div>
          <button class="prediction-clear" (click)="onClearPrediction()">Clear</button>
        </div>
        <div class="prediction-value-row">
          <dojo-markdown-viewer [content]="predictionFormula()" />
        </div>
      </div>
    }
  </div>
}
